import { useContext, useEffect } from 'react';
import AuthorizationContext from 'components/shared/Authorization/AuthorizationContext';

export function checkPermissions(permissionsToCheck = [], permissions = {}) {
  return permissionsToCheck.every(
    ({ type, action }) => permissions[type] && permissions[type][action],
  );
}

/**
 * usePermissions is a React hook that allows functional components to hook check the current users permissions to validate if
 * if permitted on the certain policy. We use `type`, lower snakecase of the backend policy name (e.g. content -> ContentPolicy),
 * and the `action`, lower snakecase of our PunditPolicy methods (e.g. create -> create?) to check our `/permissions` lookup to if
 * user isPermitted or not.
 *
 * This utilize the AuthorizationProvider and `/permissions` endpoint that builds a list of permissions
 * from permitted actions on pundit policies.
 *
 * @memberof module:React
 * @param {Array|Object} permissions - Permission object or array of permission objects that will be checked if permitted
 *
 * @example
 * const {
 *   isPermitted: hasAssignmentCreatePermission,
 *   isLoading,
 *   error,
 *   permissions,
 * } = usePermissions({ type: 'assignment', action: 'create' });
 *
 */
export default function usePermissions(permissions = []) {
  // Check out AuthorizationProvider for More Information
  const state = useContext(AuthorizationContext);

  useEffect(() => {
    state.initializePermissions({
      debug: process.env.NODE_ENV === 'development',
    });
  });

  let permissionsToCheck = permissions;
  if (!Array.isArray(permissions) && typeof permissions === 'object') {
    permissionsToCheck = [permissions];
  }

  return {
    ...state,
    isPermitted: checkPermissions(permissionsToCheck, state.permissions),
  };
}
